<template>
  <section id="form-quote-tiers" class="rel quote-review-section division">
    <v-container>
      <v-row>
        <v-col cols="12">
          <v-card 
            class="mx-auto align-items-center" 
            variant="flat"
          >
            <ElementsQuoteCard key="quoteContainer" :showCommands="true" :loading="quotePending" @goBack="goBack" @submitForm="submitForm"></ElementsQuoteCard>
          </v-card>
          <v-card 
            class="mx-auto align-items-center" 
            variant="flat"
          >
            <v-card-text>
              <p>The following policy benefits are as approved in most states. Benefits and provisions may vary by state. For complete details of coverage, please contact Assurity or review the policy and riders.</p>
              <v-data-table
                :id="'policy-benefits-table'"
                ref="refPolicyBenefitsTable"
                v-model:expanded="expanded"
                :loading="policyBenefitsPending"
                :headers="tableHeaders"
                :items="policyBenefits"
                item-value="benefitId"
                show-expand
                hover
                items-per-page="-1"
                hide-default-footer
                sticky
                @update:page="pageChange"
              >
                <template v-slot:expanded-row="{ columns, item }">
                  <tr>
                    <td :colspan="columns.length" class="pl-10">
                      {{ item.description }}
                    </td>
                  </tr>
                  <template v-if="item.children">
                  <tr v-for="(child) in item.children" :key="child.benefitId">
                    <td class="pl-10">
                      {{ child.policyBenefit }}
                    </td>
                    <td class="text-right">
                      {{ child.complete }}
                    </td>
                  </tr>
                  </template>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </section>
</template>

<script lang="ts">
import { useFetch } from 'nuxt/app';
import type { PolicyBenefit } from '~/types/types-core';
export default defineComponent({
  name: "ComponentsFormsQuoteReview",
  props: {
    stepId: {
      type: String as () => string,
      default: () => "STEP03",
    },
  },
  async setup(props: any) {
    const { stepId } = props
    const loadingQuote = ref(true)
    const applicationInfoFromStore = storeToRefs(useApplicationInfoStore()).applicationInfo.value
    const quotePayloadInfoFromStore = storeToRefs(useQuotePayloadStore()).quotePayload.value
    const tableHeaders = ref<DataTableHeader[]>([
      {
        title: 'Policy Benefits',
        key: 'policyBenefit',
        align: 'start',
        sortable: false,
      },
      {
        title: 'Amount',
        key: 'complete',
        align: 'end',
        sortable: false,
      },
      { title: '', key: 'data-table-expand' },
    ])
    const expanded = ref([])
    const currentQuoteStore = useCurrentQuoteStore()
    
    const {pending: policyBenefitsPending, data: policyBenefitsData} = useLazyFetch("/webapi/policy-benefits")
    const policyBenefits = ref<PolicyBenefit[]>([])
    watch(policyBenefitsData, (newValue: any) => {
      policyBenefits.value = newValue.sort((a: any, b: any) => a.sortIndex - b.sortIndex) ?? []
    },
    { immediate: true })

    const quotePending = ref<boolean>(false)
    const quoteData = ref([])
    const quoteError = ref<Error | null>(null)
    async function getQuote() {
      quotePending.value = true
      try {
        const response = await fetch(`/webapi/quotes`, {
          method: 'POST',
          body: JSON.stringify(quotePayloadInfoFromStore),
          headers: {
            'Content-Type': 'application/json'
          }
        })
        if (response.ok) {
          const newValue = await response.json()
          quoteData.value = newValue || []
        } else {
          quoteData.value = []
        }

      } catch (error: any) {
        quoteError.value = error
        quoteData.value = []
      } finally {
        quotePending.value = false
      }
    }
    getQuote()
    
    watch(quoteData, (newValue: any) => {
      const coveragePremium = _get(newValue, "QuoteResults.0.CoveragePremiums", []).find((item: any) => item.CoverageLookup === "Total")
      const currentQuote = ref<CurrentQuote>({
        selectedFrequency: "Monthly",
        coverage: "Complete",
        premiums: _get(coveragePremium, "Premiums", [])
      })
      currentQuoteStore.setCurrentQuoteValue(currentQuote.value)

      const monthlyPremium = ref<Premium | undefined>(_get(currentQuoteStore?.currentQuote, "premiums", []).find((item: Premium) => item.PaymentFrequency === "Monthly"))
      const quarterlyPremium = ref<Premium | undefined>(_get(currentQuoteStore?.currentQuote, "premiums", []).find((item: Premium) => item.PaymentFrequency === "Quarterly"))
      const semiAnnualPremium = ref<Premium | undefined>(_get(currentQuoteStore?.currentQuote, "premiums", []).find((item: Premium) => item.PaymentFrequency === "SemiAnnual"))
      const annualPremium = ref<Premium | undefined>(_get(currentQuoteStore?.currentQuote, "premiums", []).find((item: Premium) => item.PaymentFrequency === "Annual"))
      
      /* START - Google Analytics - Track Quote Event */
      useTrackEvent('quote', {
        age: _get(quotePayloadInfoFromStore, "product.primaryInsured.person.age", 0),
        monthlyPremiumAmount: _get(monthlyPremium, "PremiumAmount", 0),
        quarterlyPremiumAmount: _get(quarterlyPremium, "PremiumAmount", 0),
        semiAnnualPremiumAmount: _get(semiAnnualPremium, "PremiumAmount", 0),
        annualPremiumAmount: _get(annualPremium, "PremiumAmount", 0),
      })
      /* END - Google Analytics - Track Quote Event */

    })

    watch(quoteError, (newValue: Error | null) => {
      console.error('Error', newValue?.message)
    });

    const applicationInfo = ref<ApplicationInfo>(useApplicationInfo().applicationInfo.value)
    const stateAbbreviation = _get(quotePayloadInfoFromStore, "issueStateAbbreviation", "")
    const insuredOption = _get(quotePayloadInfoFromStore, "primaryInsured.coverages.0.options.insuredOption.applicationValue", "")
    if(!_isEmpty(applicationInfoFromStore)) {
      applicationInfo.value = applicationInfoFromStore
    }
    const submitForm = async () => {
      type ApplicationValue = "Individual" | "IndividualSpouse" | "IndividualChild" | "Family" | null | undefined
      let value: string = insuredOption
      applicationInfo.value.product.primaryInsured.address.stateAbbreviation = stateAbbreviation
      applicationInfo.value.product.insuredOption.applicationValue = value as ApplicationValue
      useApplicationInfoStore().setApplicationInfoValue(applicationInfo.value)
      //UPDATE CONTACT INFO - START
      const contactInfo = ref<ContactInfo>(useContactInfo().contactInfo.value);
      const contactInfoStore = storeToRefs(useContactInfoStore()).contactInfo.value
      if (!_isEmpty(contactInfoStore)) {
        contactInfo.value = contactInfoStore
      }
      const monthlyPremium = ref<Premium | undefined>()
      monthlyPremium.value = (_get(currentQuoteStore?.currentQuote, "premiums", []).find((item: Premium) => item.PaymentFrequency === "Monthly"))
      const monthlyPremiumAmount = _get(monthlyPremium?.value, "PremiumAmount", 0).toFixed(2)
      setDeepValue(contactInfo.value, 'properties.monthly_premium_emerge_app', monthlyPremiumAmount)
      useContactInfoStore().setContactInfoValue(contactInfo.value)
      try{
        await useContactInfo().putContactInfo(stepId)
      } catch(err) {
        console.error("updateContactInfo-error: ", err)
      }
      //UPDATE CONTACT INFO - END
      navigateTo({ path: '/forms/application/demographic' })
    }
    const goBack = () => {
      navigateTo({ path: '/forms/quote/tiers' })
    } 
    const refPolicyBenefitsTable = ref()
    const pageChange = (e: any) => {
      const tableElement = document.getElementById("policy-benefits-table");
      const tablePosition = (tableElement?.getBoundingClientRect()?.top || 0)
      //HANDLE DESKTOP BROWSER
      window?.scrollTo({
        top: tablePosition,
        behavior: "smooth",
      })
      //HANDLE MOBILE BROWSER
      setTimeout(() => {
        document?.body?.scrollTo({
          top: tablePosition,
          behavior: "smooth",
        })
      }, 0);
    }
    loadingQuote.value = false
    return {
      tableHeaders,
      policyBenefits,
      expanded,
      quoteData,
      submitForm,
      goBack,
      loadingQuote,
      quotePending,
      policyBenefitsPending,
      pageChange,
      refPolicyBenefitsTable
    }
  }
})
</script>
